import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, CheckCircle2, XCircle, FileOutput, Ban, Wallet, FileDown, Pencil, Mail, X, UploadCloud, Trash2, History, QrCode } from "lucide-react";
import {
  formatMoney,
  PAYMENT_METHOD_LABELS,
  SALES_DOCUMENT_STATUS_LABELS,
  SALES_DOCUMENT_STATUS_TONE,
  SALES_DOCUMENT_TYPE_LABELS,
} from "@finora/config";
import { PaymentMethod, SalesDocumentStatus, SalesDocumentType } from "@finora/types";
import { Button, StatusBadge } from "@finora/ui";
import { api } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { randomUUID } from "../../lib/uuid";
import { ApiError } from "@finora/api-client";
import { EINVOICE_STATUS_LABELS, EINVOICE_STATUS_TONE } from "@finora/config";

/**
 * PDF open parameter (standard, podržan u Chrome/Firefox/Edge PDF viewer-u)
 * - "uklopi širinu, prikaži od vrha stranice" umesto podrazumevanog
 * "Automatic" zuma koji dokument centrira kao mali pravougaonik usred
 * praznog prostora. Prijavio korisnik (28.09.2026) - i za Preuzmi PDF i za
 * PDF otpremnice.
 */
const PDF_TOP_ALIGNED_VIEW = "#view=FitH";

/** Objašnjava korisniku šta je logičan sledeći korak za trenutni status/tip dokumenta. */
function getNextStepHint(
  status: SalesDocumentStatus,
  isQuote: boolean,
): string | null {
  if (status === SalesDocumentStatus.DRAFT && isQuote) {
    return "Sledeći korak: dodajte stavke, pa kliknite „Pošalji” da ponuda ode kupcu.";
  }
  if (status === SalesDocumentStatus.DRAFT && !isQuote) {
    return "Sledeći korak: kliknite „Odobri”, pa „Pošalji” da faktura ode kupcu.";
  }
  if (status === SalesDocumentStatus.APPROVED && !isQuote) {
    return "Sledeći korak: kliknite „Pošalji” da faktura ode kupcu.";
  }
  if (isQuote && (status === SalesDocumentStatus.SENT || status === SalesDocumentStatus.VIEWED)) {
    return "Čeka se odluka kupca. Kupac može sam da odgovori preko javnog linka, ili ovde ručno označite „Prihvaćena”/„Odbijena”.";
  }
  if (isQuote && status === SalesDocumentStatus.ACCEPTED) {
    return "Ponuda je prihvaćena. Sledeći korak: kliknite „Pretvori u fakturu”.";
  }
  if (isQuote && status === SalesDocumentStatus.CONVERTED) {
    return "Sve stavke ove ponude su već fakturisane.";
  }
  if (isQuote && status === SalesDocumentStatus.REJECTED) {
    return "Kupac je odbio ovu ponudu.";
  }
  if (!isQuote && status === SalesDocumentStatus.SENT) {
    return "Faktura je poslata kupcu - čeka se uplata (i, ako je potrebno, slanje na SEF).";
  }
  if (!isQuote && status === SalesDocumentStatus.PARTIALLY_PAID) {
    return "Faktura je delimično plaćena - preostali iznos je prikazan ispod.";
  }
  if (!isQuote && status === SalesDocumentStatus.OVERDUE) {
    return "Rok plaćanja je prošao, a faktura još nije u potpunosti plaćena.";
  }
  if (!isQuote && status === SalesDocumentStatus.PAID) {
    return "Faktura je u potpunosti plaćena.";
  }
  if (status === SalesDocumentStatus.CANCELLED) {
    return "Ovaj dokument je storniran i više se ne može menjati.";
  }
  return null;
}

export function SalesDocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [paymentAmount, setPaymentAmount] = useState("");
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState<"ok" | "failed" | null>(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  // "Otkaži eFakturu" (razgovor 28.09.2026, Task 6) - backend
  // (POST /e-invoices/sef/:id/cancel) već postoji i zahteva obavezan
  // razlog/komentar (cancelSefInvoiceSchema), ali do sada nije bilo dugmeta
  // u UI-ju. Isti obrazac modala kao "Pošalji emailom" iznad.
  const [sefCancelModalOpen, setSefCancelModalOpen] = useState(false);
  const [sefCancelComment, setSefCancelComment] = useState("");

  const { data: doc, isLoading } = useQuery({
    queryKey: ["sales-document", id],
    queryFn: () => api.getSalesDocument(id!),
    enabled: !!id,
  });

  const invalidate = (updated: typeof doc) => {
    if (!updated) return;
    queryClient.setQueryData(["sales-document", updated.id], updated);
    queryClient.invalidateQueries({ queryKey: ["sales-documents"] });
  };

  const send = useMutation({ mutationFn: () => api.sendSalesDocument(id!), onSuccess: invalidate });
  const approve = useMutation({ mutationFn: () => api.approveSalesDocument(id!), onSuccess: invalidate });
  const accept = useMutation({ mutationFn: () => api.acceptSalesDocument(id!), onSuccess: invalidate });
  const reject = useMutation({ mutationFn: () => api.rejectSalesDocument(id!), onSuccess: invalidate });
  const cancel = useMutation({ mutationFn: () => api.cancelSalesDocument(id!), onSuccess: invalidate });

  const deleteDoc = useMutation({
    mutationFn: () => api.deleteSalesDocument(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-documents"] });
      navigate("/prodaja");
    },
  });

  const convert = useMutation({
    mutationFn: () => api.convertQuoteToInvoice(id!, { idempotencyKey: randomUUID() }),
    onSuccess: (invoice) => {
      queryClient.invalidateQueries({ queryKey: ["sales-documents"] });
      navigate(`/prodaja/${invoice.id}`);
    },
  });

  const sendEmail = useMutation({
    mutationFn: () =>
      api.sendSalesDocumentEmail(id!, {
        to: emailTo || undefined,
        message: emailMessage || undefined,
      }),
    onSuccess: (updated) => {
      invalidate(updated);
      setEmailModalOpen(false);
      setEmailMessage("");
    },
  });

  const sendToSef = useMutation({
    mutationFn: () => api.sendSalesDocumentToSef(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["sef-status-for-doc", id] }),
  });

  const { data: ipsTransactions } = useQuery({
    queryKey: ["ips-transactions", id],
    queryFn: () => api.getIpsTransactions(id!),
    enabled: !!id,
  });

  const { data: otpCardTransactions } = useQuery({
    queryKey: ["otp-card-transactions", id],
    queryFn: () => api.getOtpCardTransactions(id!),
    enabled: !!id,
  });

  const { data: sefStatus } = useQuery({
    queryKey: ["sef-status-for-doc", id],
    queryFn: () => api.getSefStatusForDocument(id!),
    enabled: !!id,
  });

  const cancelSefInvoice = useMutation({
    mutationFn: () => api.cancelSefInvoice(sefStatus!.id, sefCancelComment.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sef-status-for-doc", id] });
      setSefCancelModalOpen(false);
      setSefCancelComment("");
    },
  });

  const { data: deliveryNote } = useQuery({
    queryKey: ["delivery-note-for-doc", id],
    queryFn: () => api.getDeliveryNoteForDocument(id!),
    enabled: !!id && doc?.type === SalesDocumentType.INVOICE,
  });

  const { data: companySettings } = useQuery({
    queryKey: ["company-settings"],
    queryFn: () => api.getCompanySettings(),
  });

  const { data: issueOrder } = useQuery({
    queryKey: ["warehouse-issue-order-for-doc", id],
    queryFn: () => api.createWarehouseIssueOrder(id!),
    enabled:
      !!companySettings?.usesWarehouse &&
      doc?.type !== SalesDocumentType.QUOTE &&
      doc?.documentNumber != null,
  });

  const createDeliveryNote = useMutation({
    mutationFn: () => api.createDeliveryNote(id!),
    onSuccess: (note) => queryClient.setQueryData(["delivery-note-for-doc", id], note),
  });

  const sendDeliveryNoteEmail = useMutation({
    mutationFn: () => api.sendDeliveryNoteEmail(deliveryNote!.id),
  });

  const downloadPdf = useMutation({
    mutationFn: () => api.downloadSalesDocumentPdfBlobUrl(id!),
    onSuccess: (blobUrl) => {
      // 28.09.2026: dodat #view=FitH da PDF ne bude centriran u praznom
      // prostoru - vidi PDF_TOP_ALIGNED_VIEW gore.
      window.open(blobUrl + PDF_TOP_ALIGNED_VIEW, "_blank");
    },
  });

  const [deliveryLinkCopied, setDeliveryLinkCopied] = useState<"ok" | "failed" | null>(null);

  const registerPayment = useMutation({
    mutationFn: () =>
      api.registerSalesDocumentPayment(id!, {
        method: PaymentMethod.BANK_TRANSFER,
        amount: paymentAmount,
      }),
    onSuccess: (updated) => {
      invalidate(updated);
      setPaymentAmount("");
    },
  });

  if (isLoading || !doc) {
    return <p className="text-sm text-slate-500">Učitavanje...</p>;
  }

  const isQuote = doc.type === SalesDocumentType.QUOTE;
  // Gate dugmeta "Otkaži eFakturu" - vidljivo samo kada je faktura STVARNO
  // poslata na SEF (ima sefInvoiceId) i status nije već terminalan
  // (REJECTED/CANCELLED nemaju smisla otkazati ponovo).
  const sefCancellable =
    !!sefStatus?.sefInvoiceId && !["REJECTED", "CANCELLED"].includes(sefStatus.status);

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate("/prodaja")}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Nazad na Prodaju
      </button>

      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-slate-900">
              {doc.documentNumber ?? `Nacrt ${SALES_DOCUMENT_TYPE_LABELS[doc.type]}`}
            </h1>
            <StatusBadge
              label={SALES_DOCUMENT_STATUS_LABELS[doc.status]}
              tone={SALES_DOCUMENT_STATUS_TONE[doc.status]}
            />
          </div>
          <p className="text-sm text-slate-500">
            {SALES_DOCUMENT_TYPE_LABELS[doc.type]} · {doc.customer?.name ?? "Bez kupca"}
          </p>
          {getNextStepHint(doc.status, isQuote) && (
            <p className="mt-1 text-sm text-slate-600">
              💡 {getNextStepHint(doc.status, isQuote)}
            </p>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            disabled={downloadPdf.isPending}
            onClick={() => downloadPdf.mutate()}
          >
            <FileDown className="h-4 w-4" aria-hidden /> Preuzmi PDF
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setEmailTo(doc.customer?.email ?? "");
              setEmailModalOpen(true);
            }}
          >
            <Mail className="h-4 w-4" aria-hidden /> Pošalji emailom
          </Button>
          {!isQuote && doc.documentNumber && (!sefStatus || sefStatus.status === "REJECTED") && (
            <Button
              variant="secondary"
              disabled={sendToSef.isPending}
              onClick={() => sendToSef.mutate()}
            >
              <UploadCloud className="h-4 w-4" aria-hidden />
              {sendToSef.isPending ? "Šaljem na SEF..." : "Pošalji na SEF"}
            </Button>
          )}
          {sefCancellable && (
            <Button
              variant="ghost"
              onClick={() => {
                setSefCancelComment("");
                setSefCancelModalOpen(true);
              }}
            >
              <Ban className="h-4 w-4" aria-hidden /> Otkaži eFakturu
            </Button>
          )}
          {doc.status === SalesDocumentStatus.DRAFT && (
            <Button variant="secondary" onClick={() => navigate(`/prodaja/${doc.id}/izmeni`)}>
              <Pencil className="h-4 w-4" aria-hidden /> Izmeni
            </Button>
          )}
          {doc.status === SalesDocumentStatus.DRAFT && isQuote && (
            <Button onClick={() => send.mutate()} disabled={send.isPending}>
              <Send className="h-4 w-4" aria-hidden /> Pošalji
            </Button>
          )}
          {doc.status === SalesDocumentStatus.DRAFT && !isQuote && (
            <Button onClick={() => approve.mutate()} disabled={approve.isPending}>
              <CheckCircle2 className="h-4 w-4" aria-hidden /> Odobri
            </Button>
          )}
          {doc.status === SalesDocumentStatus.APPROVED && !isQuote && (
            <Button onClick={() => send.mutate()} disabled={send.isPending}>
              <Send className="h-4 w-4" aria-hidden /> Pošalji
            </Button>
          )}
          {isQuote &&
            ([SalesDocumentStatus.SENT, SalesDocumentStatus.VIEWED] as SalesDocumentStatus[]).includes(
              doc.status,
            ) && (
            <>
              <Button variant="success" onClick={() => accept.mutate()} disabled={accept.isPending}>
                <CheckCircle2 className="h-4 w-4" aria-hidden /> Prihvaćena
              </Button>
              <Button variant="danger" onClick={() => reject.mutate()} disabled={reject.isPending}>
                <XCircle className="h-4 w-4" aria-hidden /> Odbijena
              </Button>
            </>
          )}
          {isQuote && doc.status === SalesDocumentStatus.ACCEPTED && (
            <Button variant="success" onClick={() => convert.mutate()} disabled={convert.isPending}>
              <FileOutput className="h-4 w-4" aria-hidden /> Pretvori u fakturu
            </Button>
          )}
          {(
            [
              SalesDocumentStatus.DRAFT,
              SalesDocumentStatus.APPROVED,
              SalesDocumentStatus.SENT,
            ] as SalesDocumentStatus[]
          ).includes(doc.status) && (
            <Button variant="ghost" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
              <Ban className="h-4 w-4" aria-hidden /> Storniraj
            </Button>
          )}
          {!(
            [
              SalesDocumentStatus.ACCEPTED,
              SalesDocumentStatus.CONVERTED,
              SalesDocumentStatus.PARTIALLY_PAID,
              SalesDocumentStatus.PAID,
              SalesDocumentStatus.OVERDUE,
            ] as SalesDocumentStatus[]
          ).includes(doc.status) && (
            <Button
              variant="ghost"
              onClick={() => {
                if (window.confirm("Da li sigurno želite trajno da obrišete ovaj dokument?")) {
                  deleteDoc.mutate();
                }
              }}
              disabled={deleteDoc.isPending}
            >
              <Trash2 className="h-4 w-4" aria-hidden /> Obriši
            </Button>
          )}
        </div>
      </div>

      {convert.isError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {convert.error instanceof ApiError
            ? convert.error.response.message
            : "Pretvaranje u fakturu nije uspelo. Pokušajte ponovo."}
        </p>
      )}

      {/* Bug fix (22.09.2026, prijavio korisnik: "kada pokusam da obrisem
          storniranu fakturu prihvati ali je ne obrise") - brisanje je do
          sada tiho propadalo na grešku sa servera (npr. dokument ima
          povezanu otpremnicu/nalog magacinu) bez ikakve poruke korisniku. */}
      {deleteDoc.isError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {deleteDoc.error instanceof ApiError
            ? deleteDoc.error.response.message
            : "Brisanje dokumenta nije uspelo. Pokušajte ponovo."}
        </p>
      )}

      {!isQuote && doc.documentNumber && (
        <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700">Status na SEF-u:</span>
              {sefStatus ? (
                <StatusBadge
                  label={EINVOICE_STATUS_LABELS[sefStatus.status as keyof typeof EINVOICE_STATUS_LABELS] ?? sefStatus.status}
                  tone={EINVOICE_STATUS_TONE[sefStatus.status as keyof typeof EINVOICE_STATUS_TONE] ?? "neutral"}
                />
              ) : (
                <StatusBadge label="Nije poslato" tone="neutral" />
              )}
            </div>
            {sefStatus && sefStatus.status !== "REJECTED" && (
              <button
                onClick={() =>
                  api
                    .refreshSefStatus(sefStatus.id)
                    .then(() => queryClient.invalidateQueries({ queryKey: ["sef-status-for-doc", id] }))
                }
                className="text-xs font-medium text-brand hover:underline"
              >
                Osveži status
              </button>
            )}
          </div>
          {sefStatus?.sefInvoiceId && (
            <p className="mt-1 text-xs text-slate-400">SEF broj: {sefStatus.sefInvoiceId}</p>
          )}
          {sefStatus?.rejectionReason && (
            <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {sefStatus.rejectionReason}
            </p>
          )}
          {sendToSef.isError && (
            <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {sendToSef.error instanceof ApiError
                ? sendToSef.error.response.message
                : "Slanje na SEF nije uspelo."}
            </p>
          )}
          {cancelSefInvoice.isError && (
            <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {cancelSefInvoice.error instanceof ApiError
                ? cancelSefInvoice.error.response.message
                : "Otkazivanje eFakture nije uspelo."}
            </p>
          )}
        </div>
      )}

      {!isQuote && (
        <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Otpremnica</span>
            {!deliveryNote ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={createDeliveryNote.isPending}
                onClick={() => createDeliveryNote.mutate()}
              >
                Kreiraj otpremnicu
              </Button>
            ) : (
              <StatusBadge
                label={deliveryNote.status === "CONFIRMED" ? "Prijem potvrđen" : "Poslato, čeka potvrdu"}
                tone={deliveryNote.status === "CONFIRMED" ? "success" : "warning"}
              />
            )}
          </div>
          {deliveryNote && (
            <>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 truncate text-xs text-slate-500">
                  {window.location.origin}/pregled/otpremnica/{deliveryNote.publicLinkToken}
                </code>
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await copyToClipboard(
                      `${window.location.origin}/pregled/otpremnica/${deliveryNote.publicLinkToken}`,
                    );
                    setDeliveryLinkCopied(ok ? "ok" : "failed");
                    setTimeout(() => setDeliveryLinkCopied(null), 2000);
                  }}
                  className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-hover"
                >
                  {deliveryLinkCopied === "ok" ? "Kopirano ✓" : "Kopiraj"}
                </button>
                <a
                  href={api.getPublicDeliveryNotePdfUrl(deliveryNote.publicLinkToken) + PDF_TOP_ALIGNED_VIEW}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  PDF
                </a>
                {deliveryNote.status !== "CONFIRMED" && (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={sendDeliveryNoteEmail.isPending}
                    onClick={() => sendDeliveryNoteEmail.mutate()}
                  >
                    {sendDeliveryNoteEmail.isPending ? "Šaljem..." : "Pošalji emailom"}
                  </Button>
                )}
              </div>
              {sendDeliveryNoteEmail.isSuccess && (
                <p className="mt-2 text-xs text-emerald-600">Email sa linkom i QR kôdom je poslat.</p>
              )}
              {sendDeliveryNoteEmail.isError && (
                <p className="mt-2 text-xs text-red-600">
                  {sendDeliveryNoteEmail.error instanceof ApiError
                    ? sendDeliveryNoteEmail.error.response.message
                    : "Slanje nije uspelo."}
                </p>
              )}
              {deliveryNote.status === "CONFIRMED" && (
                <p className="mt-2 text-xs text-slate-500">
                  Potvrdio: {deliveryNote.confirmedByName} {deliveryNote.confirmedBySurname}, lična karta{" "}
                  {deliveryNote.confirmedByIdCardNumber} ({deliveryNote.confirmedByIdCardIssuePlace})
                  {deliveryNote.confirmedAt && (
                    <> · {new Date(deliveryNote.confirmedAt).toLocaleString("sr-RS")}</>
                  )}
                  {deliveryNote.confirmedByIp && <> · IP: {deliveryNote.confirmedByIp}</>}
                </p>
              )}
              {deliveryNote.confirmedNote && (
                <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <span className="font-medium">Napomena kupca: </span>
                  {deliveryNote.confirmedNote}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {!isQuote && companySettings?.usesWarehouse && issueOrder && (
        <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Nalog magacinu</span>
            <StatusBadge
              label={
                issueOrder.status === "COMPLETED"
                  ? "Izvršeno"
                  : issueOrder.status === "PARTIALLY_ISSUED"
                    ? "Delimično izdato"
                    : "Čeka izdavanje"
              }
              tone={
                issueOrder.status === "COMPLETED"
                  ? "success"
                  : issueOrder.status === "PARTIALLY_ISSUED"
                    ? "warning"
                    : "neutral"
              }
            />
          </div>
          <p className="mt-2 text-xs text-slate-400">
            Upravljanje izdavanjem (uključujući serijske brojeve) na Magacinsko poslovanje → Nalozi
            magacinu.
          </p>
        </div>
      )}

      {isQuote && doc.publicLinkToken && (
        <div className="flex items-center gap-2 rounded-xl border border-brand-border bg-brand-light px-4 py-3 text-sm">
          <span className="font-medium text-slate-700">Javni link za kupca:</span>
          <code className="flex-1 truncate text-slate-600">
            {window.location.origin}/pregled/ponuda/{doc.publicLinkToken}
          </code>
          <button
            type="button"
            onClick={async () => {
              const ok = await copyToClipboard(
                `${window.location.origin}/pregled/ponuda/${doc.publicLinkToken}`,
              );
              setLinkCopied(ok ? "ok" : "failed");
              setTimeout(() => setLinkCopied(null), 2000);
            }}
            className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-hover"
          >
            {linkCopied === "ok" ? "Kopirano ✓" : linkCopied === "failed" ? "Nije uspelo" : "Kopiraj"}
          </button>
        </div>
      )}

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        <table>
          <thead>
            <tr>
              <th>Naziv</th>
              <th>Količina</th>
              <th>Cena</th>
              <th>PDV</th>
              <th>Ukupno</th>
              {isQuote && <th>Fakturisano</th>}
            </tr>
          </thead>
          <tbody>
            {doc.items.map((item) => (
              <tr key={item.id}>
                <td className="font-medium text-slate-800">{item.name}</td>
                <td>
                  {item.quantity} {item.unit}
                </td>
                <td>{formatMoney(item.unitPriceGross)}</td>
                <td>{item.taxRatePercent}%</td>
                <td className="font-medium">{formatMoney(item.grossAmount)}</td>
                {isQuote && (
                  <td className="text-slate-500">
                    {item.invoicedQuantity} / {item.quantity}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end">
        <div className="w-72 space-y-1 rounded-xl border border-border bg-surface p-4 text-sm shadow-subtle">
          <div className="flex justify-between text-slate-500">
            <span>Osnovica</span>
            <span>{formatMoney(doc.netAmount)}</span>
          </div>
          <div className="flex justify-between text-slate-500">
            <span>PDV</span>
            <span>{formatMoney(doc.taxAmount)}</span>
          </div>
          {Number(doc.discountAmount) > 0 && (
            <div className="flex justify-between text-amber-600">
              <span>Popust</span>
              <span>-{formatMoney(doc.discountAmount)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-semibold text-slate-900">
            <span>Ukupno</span>
            <span>{formatMoney(doc.grossAmount)}</span>
          </div>
          {!isQuote && (
            <>
              <div className="flex justify-between text-emerald-600">
                <span>Plaćeno</span>
                <span>{formatMoney(doc.paidAmount)}</span>
              </div>
              <div className="flex justify-between font-medium text-amber-600">
                <span>Preostalo</span>
                <span>{formatMoney(doc.dueAmount)}</span>
              </div>
            </>
          )}
          {isQuote && (
            <div className="flex justify-between text-slate-500">
              <span>Fakturisano</span>
              <span>{formatMoney(doc.invoicedAmount)}</span>
            </div>
          )}
        </div>
      </div>

      {!isQuote && Number(doc.dueAmount) > 0 && (
        <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Wallet className="h-4 w-4" aria-hidden /> Evidentiraj uplatu
          </h2>
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-xs font-medium text-slate-500">Iznos</label>
              <input
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder={doc.dueAmount}
                className="mt-1 h-10 w-40 rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
              />
            </div>
            <Button
              disabled={!paymentAmount || registerPayment.isPending}
              onClick={() => registerPayment.mutate()}
            >
              Evidentiraj ({PAYMENT_METHOD_LABELS[PaymentMethod.BANK_TRANSFER]})
            </Button>
          </div>
          {doc.payments.length > 0 && (
            <ul className="mt-4 space-y-2 text-sm text-slate-500">
              {doc.payments.map((p) => {
                const meta = (p as { metadata?: Record<string, unknown> | null }).metadata;
                const source = meta?.source as string | undefined;
                return (
                  <li key={p.id} className="border-b border-border/60 pb-2 last:border-0 last:pb-0">
                    <div className="flex justify-between">
                      <span>
                        {new Date(p.createdAt).toLocaleDateString("sr-RS")} · {PAYMENT_METHOD_LABELS[p.method]}
                        {source === "IPS_PGW" && " · IPS QR"}
                        {source === "OTP_CARD" && " · Kartica (OTP)"}
                        {source === "BANK_STATEMENT" && " · Uvoz izvoda"}
                      </span>
                      <span className="font-medium text-slate-800">{formatMoney(p.amount)}</span>
                    </div>
                    {p.note && <div className="mt-0.5 text-xs text-slate-400">{p.note}</div>}
                    {source === "BANK_STATEMENT" && (
                      <div className="mt-1 rounded-md bg-app-bg px-2 py-1.5 text-xs text-slate-500">
                        {meta?.payerName ? <div>Nalogodavac: {String(meta.payerName)}</div> : null}
                        {meta?.description ? <div>Opis: {String(meta.description)}</div> : null}
                        {meta?.referenceNumber ? <div>Poziv na broj: {String(meta.referenceNumber)}</div> : null}
                        {meta?.valueDate ? (
                          <div>Datum valute: {new Date(String(meta.valueDate)).toLocaleDateString("sr-RS")}</div>
                        ) : null}
                      </div>
                    )}
                    {source === "IPS_PGW" && (
                      <div className="mt-1 rounded-md bg-app-bg px-2 py-1.5 text-xs text-slate-500">
                        {meta?.responseCode ? <div>Kod odgovora: {String(meta.responseCode)}</div> : null}
                        {meta?.paymentReference ? <div>Referenca: {String(meta.paymentReference)}</div> : null}
                      </div>
                    )}
                    {source === "OTP_CARD" && (
                      <div className="mt-1 rounded-md bg-app-bg px-2 py-1.5 text-xs text-slate-500">
                        {meta?.maskedPan ? <div>Kartica: {String(meta.maskedPan)}{meta?.cardType ? ` (${String(meta.cardType)})` : ""}</div> : null}
                        {meta?.authNumber ? <div>Broj autorizacije: {String(meta.authNumber)}</div> : null}
                        {meta?.resultCode ? <div>Kod rezultata: {String(meta.resultCode)}</div> : null}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {!isQuote && ipsTransactions && ipsTransactions.length > 0 && (
        <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <History className="h-4 w-4" aria-hidden /> IPS QR transakcije (log pokušaja plaćanja)
          </h2>
          <ul className="space-y-2 text-sm text-slate-500">
            {ipsTransactions.map((t) => (
              <li key={t.id} className="flex items-center justify-between border-b border-border/60 pb-2 last:border-0 last:pb-0">
                <div className="flex items-center gap-2">
                  <QrCode className="h-3.5 w-3.5 text-slate-400" aria-hidden />
                  <div>
                    <div>{new Date(t.createdAt).toLocaleString("sr-RS")}</div>
                    {t.responseCode && <div className="text-xs text-slate-400">Kod odgovora: {t.responseCode}</div>}
                    {t.paymentReference && <div className="text-xs text-slate-400">Referenca: {t.paymentReference}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-slate-800">{formatMoney(t.amount)}</span>
                  <StatusBadge
                    label={t.status === "SUCCESS" ? "Uspešno" : t.status === "FAILED" ? "Neuspešno" : "U toku"}
                    tone={t.status === "SUCCESS" ? "success" : t.status === "FAILED" ? "error" : "neutral"}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isQuote && otpCardTransactions && otpCardTransactions.length > 0 && (
        <div className="rounded-xl border border-border bg-surface p-5 shadow-subtle">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <History className="h-4 w-4" aria-hidden /> Kartične transakcije (OTP Banka) - log pokušaja plaćanja
          </h2>
          <ul className="space-y-2 text-sm text-slate-500">
            {otpCardTransactions.map((t) => (
              <li key={t.id} className="flex items-center justify-between border-b border-border/60 pb-2 last:border-0 last:pb-0">
                <div className="flex items-center gap-2">
                  <Wallet className="h-3.5 w-3.5 text-slate-400" aria-hidden />
                  <div>
                    <div>{new Date(t.createdAt).toLocaleString("sr-RS")}</div>
                    {t.maskedPan && (
                      <div className="text-xs text-slate-400">
                        Kartica: {t.maskedPan}
                        {t.cardType ? ` (${t.cardType})` : ""}
                      </div>
                    )}
                    {t.authNumber && <div className="text-xs text-slate-400">Broj autorizacije: {t.authNumber}</div>}
                    {t.resultCode && <div className="text-xs text-slate-400">Kod rezultata: {t.resultCode}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-slate-800">{formatMoney(t.amount)}</span>
                  <StatusBadge
                    label={t.status === "SUCCESS" ? "Uspešno" : t.status === "FAILED" ? "Neuspešno" : "U toku"}
                    tone={t.status === "SUCCESS" ? "success" : t.status === "FAILED" ? "error" : "neutral"}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {emailModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="flex w-full max-w-md flex-col rounded-xl bg-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h2 className="text-base font-semibold text-slate-900">Pošalji emailom</h2>
              <button
                type="button"
                aria-label="Zatvori"
                onClick={() => setEmailModalOpen(false)}
                className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <div className="space-y-4 px-5 py-5">
              <div>
                <label className="block text-sm font-medium text-slate-700">Email adresa</label>
                <input
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="kupac@example.com"
                  className="mt-1 h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Poruka (opciono)</label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={3}
                  placeholder="U prilogu Vam šaljemo dokument."
                  className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
                />
              </div>
              {sendEmail.isError && (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                  {sendEmail.error instanceof ApiError
                    ? sendEmail.error.response.message
                    : "Slanje nije uspelo. Proverite da li je email servis podešen na serveru (SMTP), ili pokušajte ponovo."}
                </p>
              )}
            </div>
            <div className="flex gap-3 border-t border-border px-5 py-4">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setEmailModalOpen(false)}
                disabled={sendEmail.isPending}
              >
                Otkaži
              </Button>
              <Button
                className="flex-1"
                disabled={!emailTo || sendEmail.isPending}
                onClick={() => sendEmail.mutate()}
              >
                {sendEmail.isPending ? "Šaljem..." : "Pošalji"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {sefCancelModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="flex w-full max-w-md flex-col rounded-xl bg-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h2 className="text-base font-semibold text-slate-900">Otkaži eFakturu</h2>
              <button
                type="button"
                aria-label="Zatvori"
                onClick={() => setSefCancelModalOpen(false)}
                className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <div className="space-y-4 px-5 py-5">
              <p className="text-sm text-slate-600">
                Ova akcija otkazuje fakturu na SEF-u (nepovratno). Unesite razlog otkazivanja - SEF
                zahteva obavezan komentar.
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700">Razlog otkazivanja</label>
                <textarea
                  value={sefCancelComment}
                  onChange={(e) => setSefCancelComment(e.target.value)}
                  rows={3}
                  placeholder="Npr. Faktura je pogrešno izdata."
                  className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none"
                />
              </div>
              {cancelSefInvoice.isError && (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                  {cancelSefInvoice.error instanceof ApiError
                    ? cancelSefInvoice.error.response.message
                    : "Otkazivanje eFakture nije uspelo. Pokušajte ponovo."}
                </p>
              )}
            </div>
            <div className="flex gap-3 border-t border-border px-5 py-4">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setSefCancelModalOpen(false)}
                disabled={cancelSefInvoice.isPending}
              >
                Odustani
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                disabled={!sefCancelComment.trim() || cancelSefInvoice.isPending}
                onClick={() => cancelSefInvoice.mutate()}
              >
                {cancelSefInvoice.isPending ? "Otkazujem..." : "Otkaži eFakturu"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
